import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { CircularProgress } from "@mui/material";

import ErrorPage from "../Components/Others/error-page";

import renderSelectConnectedAccounts from "../Organisms/Modals/Bank Account/select-connected-accounts";
import renderCreditBuilderOptIn from "../Organisms/Modals/Credit-Builder/credit-builder-opt-in";
import renderApplyStakeChecking from "../Organisms/Modals/Stake Checking/apply-stake-checking";
import renderJoinStakeSaver from "../Organisms/Modals/Stake Saver/join-stake-saver";
import renderColoradoRenterRewards from "../Organisms/Modals/Programs/colorado-renter-rewards";

import { useAppUtilityContext } from "./AppUtilityContext";
import { useModalContext } from "./ModalContext";

import usePrefetchOnLogin from "../CustomHooks/usePrefetchOnLogin";

import { getHasVisitedWelcomeScreenKey, isOnboardingModule, LocalStorage } from "../Utils/helpers";
import { APIQueryKeys, appUtilityKeys } from "../Utils/constants";

const RedirectContext = createContext();

export const RedirectContextProvider = () => {
  const [hasVisitedWelcomeScreen, setHasVisitedWelcomeScreen] = useState(
    LocalStorage.read(getHasVisitedWelcomeScreenKey()),
  );

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const modalContext = useModalContext();

  const { onUserCredentialsUpdate, appUtilityProps, setAppUtilityProps } = useAppUtilityContext();

  const {
    isLoading: isPrefetchingData,
    isSuccess: isDataPrefetchSuccess,
    isError: isDataPrefetchError,
    data: { dealsData, consumerDealsData, appEssentialsData, programsData },
    hasUnclaimedTransactionsData,
    errorStatus,
  } = usePrefetchOnLogin({ authKey: appUtilityProps.authKey, hasVisitedWelcomeScreen });

  const onLogin = (data) => {
    queryClient.resetQueries();
    onUserCredentialsUpdate(data);
    setHasVisitedWelcomeScreen(LocalStorage.read(getHasVisitedWelcomeScreenKey()));
  };

  const markVisitedWelcomePage = () => {
    LocalStorage.write(getHasVisitedWelcomeScreenKey(), true);
    setHasVisitedWelcomeScreen(true);
  };

  useEffect(() => {
    const offers = consumerDealsData?.data?.data?.offers ?? [];

    if (isDataPrefetchSuccess) {
      if (!offers.length > 0) {
        setAppUtilityProps((p) => ({ ...p, hideDealsRoutes: true }));
      }
    }
    //eslint-disable-next-line
  }, [isDataPrefetchSuccess, consumerDealsData]);

  useEffect(() => {
    if (!!appUtilityProps.authKey) {
      queryClient.refetchQueries(APIQueryKeys.deals);
      queryClient.refetchQueries(APIQueryKeys.userAccount);
      queryClient.refetchQueries(APIQueryKeys.transferToken);
      if (!hasVisitedWelcomeScreen) {
        queryClient.refetchQueries(APIQueryKeys.mobileAppSummary);
        queryClient.refetchQueries(APIQueryKeys.platformSummaryStats);
      }
    }
  }, [queryClient, appUtilityProps.authKey, hasVisitedWelcomeScreen]);

  useEffect(
    () => {
      if (isOnboardingModule(location.pathname) && !isPrefetchingData && isDataPrefetchSuccess) {
        const deepLinkPath = LocalStorage.read(appUtilityKeys.deeplink);

        if (deepLinkPath === "beginConnectBank") {
          renderSelectConnectedAccounts(modalContext, { openPlaidOnMount: true });
        } else if (deepLinkPath === "beginCreditBuilder") {
          renderCreditBuilderOptIn(modalContext);
        } else if (deepLinkPath === "beginSVDApplication") {
          renderApplyStakeChecking(modalContext);
        } else if (deepLinkPath === "joinStakeSaver") {
          renderJoinStakeSaver(modalContext);
        } else if (appEssentialsData?.propertyHasProgram && appEssentialsData?.previewProgramOnLogin) {
          if (programsData?.my_program?.id === "colorado_renter_rewards") {
            renderColoradoRenterRewards(modalContext, {
              occupyFullscreen: true,
            });
          }
        }

        if (deepLinkPath === "beginAddFunds") {
          navigate("/banking/add-cash");
        } else if (deepLinkPath === "beginConnectDirectDeposit") {
          navigate("/banking/direct-deposit", { state: { openPinWheelOnMount: true } });
        } else if (location.pathname === "/welcome" || location.pathname === "/welcome/find-a-home") {
          // Don't redirect away from welcome flow - let it complete naturally
          return;
        } else if (dealsData?.data?.data?.currentDeal?.registration_status === "registered") {
          navigate("/setup-account");
        } else if (hasUnclaimedTransactionsData) {
          navigate("/unclaimed");
        } else {
          navigate("/home");
        }

        if (!!deepLinkPath) {
          LocalStorage.remove(appUtilityKeys.deeplink);
        }
      }
    }, //eslint-disable-next-line
    [
      location.pathname,
      isPrefetchingData,
      isDataPrefetchSuccess,
      hasUnclaimedTransactionsData,
      consumerDealsData,
      dealsData,
      appEssentialsData,
      programsData,
      navigate,
    ],
  );

  if (!appUtilityProps.authKey && !isOnboardingModule(location.pathname)) {
    return <Navigate to="/login" />;
  }

  const moduleType = isOnboardingModule(location.pathname) ? "onboarding" : "authenticated";

  // Don't show loading spinner when on /welcome - let the welcome flow continue uninterrupted
  const shouldShowLoading =
    isPrefetchingData && location.pathname !== "/welcome" && location.pathname !== "/welcome/find-a-home";

  return (
    <RedirectContext.Provider value={{ onLogin, markVisitedWelcomePage }}>
      {shouldShowLoading ? (
        <div className={`${moduleType}-page justify-content-center`}>
          <CircularProgress sx={{ color: "var(--mist)" }} />
        </div>
      ) : isDataPrefetchError ? (
        <ErrorPage errorStatus={errorStatus} />
      ) : (
        <Outlet />
      )}
    </RedirectContext.Provider>
  );
};

export const useRedirectContext = () => {
  const modalContextData = useContext(RedirectContext);
  if (modalContextData === undefined) {
    throw new Error("useRedirectContext must be used within a RedirectContextProvider");
  }
  return modalContextData;
};
